import { Link, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import ModuleLayout from '@/components/admin/ModuleLayout';
import { Toggle } from '@/components/admin/FormComponents';
import { LoadState } from '@/components/ui/LoadState';
import { EmptyState } from '@/components/ui/EmptyState';
import { TrashBinIcon } from '@/icons';
import { toastError } from '@/lib/toast';
import { showUndoToast } from '@/lib/undoToast';
import { cccdAdmin, MAX_ACTIVE_SPONSORS, type EventSponsor, type SponsorTier } from '@/lib/api/cccdAdminClient';

export const TIER_LABEL: Record<SponsorTier, string> = {
  principal: 'Principal',
  aliado: 'Aliado',
  apoyo: 'Apoyo',
};

const TIER_STYLE: Record<SponsorTier, string> = {
  principal: 'bg-brand-50 text-brand-600 dark:bg-brand-500/15 dark:text-brand-400',
  aliado: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
  apoyo: 'bg-gray-50 text-gray-500 dark:bg-gray-800/60 dark:text-gray-500',
};

const iconButtonCls =
  'flex size-8 items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-30 dark:border-gray-800 dark:text-gray-400 dark:hover:bg-gray-800';

const queryKey = (eventId: string) => ['event-sponsors', eventId];

// Patrocinadores del evento que se muestran en las pantallas públicas de puntos
// (puntos-sponsors-and-live-cache). El orden dentro de cada nivel decide cuál
// aparece primero; solo se publican los activos que ya tienen logo.
export default function SponsorsPage() {
  const { eventId, regionalId } = useParams<{ eventId?: string; regionalId?: string }>();
  const isRegional = Boolean(regionalId);
  const id = (eventId ?? regionalId)!;
  // Cada evento (nacional o regional) tiene sus propios patrocinadores; la API resuelve el mismo id.
  const base = isRegional ? `/cccd/regionals/${id}` : `/cccd/events/${id}`;
  const qc = useQueryClient();

  const { data: sponsors = [], isLoading } = useQuery({
    queryKey: queryKey(id),
    queryFn: () => cccdAdmin.listSponsors(id),
  });

  const refresh = () => qc.invalidateQueries({ queryKey: queryKey(id) });
  const activeCount = sponsors.filter((s) => s.isActive).length;
  const publishedCount = sponsors.filter((s) => s.isActive && s.logoUrl).length;

  const updateMutation = useMutation({
    mutationFn: ({ sponsorId, body }: { sponsorId: string; body: Parameters<typeof cccdAdmin.updateSponsor>[1] }) =>
      cccdAdmin.updateSponsor(sponsorId, body),
    onSettled: refresh,
    onError: (err: any) => toastError(err?.response?.data?.error ?? 'No se pudo actualizar el patrocinador.'),
  });

  // Mueve un patrocinador una posición dentro de su nivel. Primero se normaliza el
  // orden del nivel (0, 10, 20…) para que dos con el mismo valor no se queden
  // empatados, y luego se intercambian los dos vecinos.
  async function move(sponsor: EventSponsor, direction: -1 | 1) {
    const group = sponsors.filter((s) => s.tier === sponsor.tier);
    const from = group.findIndex((s) => s.id === sponsor.id);
    const to = from + direction;
    if (from < 0 || to < 0 || to >= group.length) return;

    const reordered = [...group];
    [reordered[from], reordered[to]] = [reordered[to], reordered[from]];

    // Optimista: la lista se reordena al instante y se corrige si falla.
    qc.setQueryData<EventSponsor[]>(queryKey(id), (prev = []) =>
      prev.map((s) => {
        const index = reordered.findIndex((r) => r.id === s.id);
        return index >= 0 ? { ...s, sortOrder: index * 10 } : s;
      }),
    );
    try {
      await Promise.all(
        reordered.map((s, index) =>
          s.sortOrder === index * 10 ? null : cccdAdmin.updateSponsor(s.id, { sortOrder: index * 10 }),
        ),
      );
    } catch (err: any) {
      toastError(err?.response?.data?.error ?? 'No se pudo cambiar el orden.');
    } finally {
      void refresh();
    }
  }

  // Elimina de inmediato en la UI y difiere el DELETE hasta que expire la ventana
  // de "Deshacer" (misma receta que el catálogo de complementos).
  function handleDelete(sponsor: EventSponsor) {
    const snapshot = sponsors;
    qc.setQueryData<EventSponsor[]>(queryKey(id), (prev = []) => prev.filter((s) => s.id !== sponsor.id));
    showUndoToast({
      message: `Patrocinador "${sponsor.name}" eliminado`,
      onUndo: () => qc.setQueryData(queryKey(id), snapshot),
      onCommit: async () => {
        try {
          await cccdAdmin.deleteSponsor(sponsor.id);
        } catch (err: any) {
          qc.setQueryData(queryKey(id), snapshot);
          toastError(err?.response?.data?.error ?? 'No se pudo eliminar el patrocinador.');
        }
      },
    });
  }

  return (
    <ModuleLayout
      breadcrumbs={[
        { label: 'SateLab', to: '/' },
        { label: 'CCCD', to: '/cccd' },
        { label: isRegional ? 'Regionales' : 'Nacionales', to: base },
        'Patrocinadores',
      ]}
      title="Patrocinadores"
      subtitle="Sus logos se muestran en las pantallas públicas de resultados y lanzamientos de este evento. Los principales van fijos y más grandes; el resto rota. Solo se publican los activos que tienen logo."
      tabs={[]}
      metrics={[]}
      actions={
        <Link
          to={`${base}/sponsors/new`}
          className="min-h-11 rounded-[10px] bg-brand-500 px-5 py-2.5 text-sm font-medium text-white hover:bg-brand-600"
        >
          + Agregar patrocinador
        </Link>
      }
    >
      <p className="mb-4 text-sm text-gray-500 dark:text-gray-400">
        {activeCount} de {MAX_ACTIVE_SPONSORS} activos · {publishedCount} publicados en pantalla
      </p>

      {isLoading ? (
        <LoadState />
      ) : sponsors.length === 0 ? (
        <EmptyState
          message="Aún no hay patrocinadores en este evento. Agrega el primero y sube su logo para que aparezca en las pantallas públicas."
          action={
            <Link to={`${base}/sponsors/new`} className="rounded-[10px] bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600">
              Agregar patrocinador
            </Link>
          }
        />
      ) : (
        <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-white/[0.03]">
          <div className="hidden grid-cols-[7.5rem_minmax(0,1fr)_6rem_5.5rem_4rem_auto] gap-4 border-b border-gray-100 bg-gray-25 px-5 py-3 md:grid dark:border-gray-800 dark:bg-white/[0.02]">
            {['Logo', 'Patrocinador', 'Nivel', 'Orden', 'Activo', ''].map((h, i) => (
              <span key={i} className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                {h}
              </span>
            ))}
          </div>

          {sponsors.map((sponsor) => {
            const group = sponsors.filter((s) => s.tier === sponsor.tier);
            const index = group.findIndex((s) => s.id === sponsor.id);
            return (
              <div
                key={sponsor.id}
                className="grid grid-cols-[7.5rem_minmax(0,1fr)] items-center gap-x-4 gap-y-3 border-b border-gray-100 px-5 py-3 last:border-b-0 md:grid-cols-[7.5rem_minmax(0,1fr)_6rem_5.5rem_4rem_auto] dark:border-gray-800"
              >
                {/* Fondo blanco: es el mismo en que se verá el logo en pantalla. */}
                <div className="flex h-12 w-[7.5rem] items-center justify-center rounded-lg border border-gray-200 bg-white p-1.5">
                  {sponsor.logoUrl ? (
                    <img src={sponsor.logoUrl} alt={`Logo de ${sponsor.name}`} className="max-h-full max-w-full object-contain" />
                  ) : (
                    <span className="text-[11px] text-gray-400">Sin logo</span>
                  )}
                </div>

                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-gray-800 dark:text-white/90">{sponsor.name}</p>
                  {!sponsor.logoUrl ? (
                    <p className="text-xs text-warning-600 dark:text-warning-400">Sin logo: no se muestra en pantalla</p>
                  ) : !sponsor.isActive ? (
                    <p className="text-xs text-gray-500 dark:text-gray-400">Inactivo: no se muestra en pantalla</p>
                  ) : null}
                </div>

                <span className={`w-fit rounded-full px-2.5 py-1 text-xs font-medium ${TIER_STYLE[sponsor.tier]}`}>
                  {TIER_LABEL[sponsor.tier]}
                </span>

                <div className="flex items-center gap-1.5">
                  <button type="button" aria-label={`Subir a ${sponsor.name}`} title="Subir" disabled={index === 0} onClick={() => void move(sponsor, -1)} className={iconButtonCls}>
                    ↑
                  </button>
                  <button
                    type="button"
                    aria-label={`Bajar a ${sponsor.name}`}
                    title="Bajar"
                    disabled={index === group.length - 1}
                    onClick={() => void move(sponsor, 1)}
                    className={iconButtonCls}
                  >
                    ↓
                  </button>
                </div>

                <Toggle
                  checked={sponsor.isActive}
                  onChange={(checked) => updateMutation.mutate({ sponsorId: sponsor.id, body: { isActive: checked } })}
                  label=""
                />

                <div className="flex items-center gap-2">
                  <Link
                    to={`${base}/sponsors/${sponsor.id}/edit`}
                    className="rounded-lg border border-gray-200 px-3 py-1.5 text-[13px] font-medium text-gray-600 hover:bg-gray-50 dark:border-gray-800 dark:text-gray-400 dark:hover:bg-gray-800"
                  >
                    Editar
                  </Link>
                  <button
                    type="button"
                    onClick={() => handleDelete(sponsor)}
                    title="Eliminar"
                    aria-label={`Eliminar a ${sponsor.name}`}
                    className="flex size-8 items-center justify-center rounded-lg border border-gray-200 text-gray-400 hover:bg-error-50 hover:text-error-600 dark:border-gray-800 dark:hover:bg-error-500/10"
                  >
                    <TrashBinIcon className="size-3.5" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </ModuleLayout>
  );
}
